/** @file Import session, choosing step: check layers, move PNG layers, switch placement, name a new project. */
import type { ImportCore, ImportDraft, ImportSession, LayerChoice } from './importTypes';

/** Applies `change` to the draft while the dialog is in its choosing step; ignored in every other step. */
const edit = (core: ImportCore, change: (draft: ImportDraft) => ImportDraft): void => {
  const step = core.store.getState();
  if (step.kind === 'choose') core.store.setState({ kind: 'choose', draft: change(step.draft) }, true);
};

/** A refused new-project name is editable in the failed step too (no project exists yet), so Retry can send another. */
const editName = (core: ImportCore, name: string): void => {
  const step = core.store.getState();
  const renamed = (d: ImportDraft): ImportDraft => (d.target.kind === 'newProject' ? { ...d, target: { kind: 'newProject', name } } : d);
  if (step.kind === 'choose') core.store.setState({ kind: 'choose', draft: renamed(step.draft) }, true);
  else if (step.kind === 'failed' && step.draft !== null) core.store.setState({ kind: 'failed', draft: renamed(step.draft), thrown: step.thrown }, true);
};

const withChoice = (draft: ImportDraft, index: number, change: (c: LayerChoice) => LayerChoice): ImportDraft => ({
  ...draft,
  choices: draft.choices.map((c) => (c.index === index ? change(c) : c)),
});

/** toggle / move / setPlace / setProjectName of the import session. */
export function editDraft(core: ImportCore): Pick<ImportSession, 'toggle' | 'move' | 'setPlace' | 'setProjectName'> {
  return {
    toggle: (index, checked) => { edit(core, (d) => withChoice(d, index, (c) => ({ ...c, checked }))); },
    move: (index, x, y) => { edit(core, (d) => (d.source.kind === 'png' ? withChoice(d, index, (c) => ({ ...c, x, y })) : d)); },
    setPlace: (place) => { edit(core, (d) => (d.source.kind === 'png' ? { ...d, place } : d)); },
    setProjectName: (name) => { editName(core, name); },
  };
}
